import React, { useEffect, useState, useMemo } from "react";
import Navbar from "../../components/admin/layout/navbar";
import Sidebar from "../../components/admin/layout/sidebar";
import { collection, getDocs, orderBy, query } from "firebase/firestore";
import { db } from "../../firebase";
import DataTable from "react-data-table-component";
import { toast } from "react-toastify";
import CustomLoader from "../../components/custom-loader";

/* ================= CUSTOM TABLE STYLE ================= */
const customStyles = {
  rows: {
    style: {
      minHeight: "55px",
    },
  },
  headCells: {
    style: {
      paddingLeft: "8px",
      paddingRight: "8px",
      backgroundColor: "#413b3b",
      color: "white",
      fontSize: "14px",
    },
  },
  cells: {
    style: {
      paddingLeft: "8px",
      paddingRight: "8px",
      borderRight: "1px solid #eaeaea",
    },
  },
};

/* ================= EXPORT BUTTON ================= */
const Export = ({ onExport }) => (
  <button
    className="bg-green-600 text-white rounded px-5 py-1.5 text-sm"
    onClick={onExport}
  >
    Export Data
  </button>
);

/* ================= MAIN COMPONENT ================= */
function FinanceLeads() {
  const [active, setActive] = useState(true);
  const [search, setSearch] = useState("");
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);

  const handleActive = () => setActive(!active);

  /* ================= FETCH DATA ================= */
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      try {
        const q = query(
          collection(db, "finance"),
          orderBy("timestamp", "desc"),
        );

        const snapshot = await getDocs(q);

        const list = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setData(list);
        setFilteredData(list);
      } catch (error) {
        console.error(error);
        toast.error("Something went wrong!");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  /* ================= SEARCH FILTER ================= */
  useEffect(() => {
    const result = data.filter((item) => {
      const phone = item.phone?.toString() || "";
      const name = item.name?.toLowerCase() || "";
      const email = item.email?.toLowerCase() || "";
      const term = search.toLowerCase();

      return (
        phone.includes(term) || name.includes(term) || email.includes(term)
      );
    });

    setFilteredData(result);
  }, [search, data]);

  /* ================= CSV EXPORT ================= */
  const downloadCSV = (array = []) => {
    if (!array.length) return;

    const keys = Object.keys(array[0]);

    const csv = [
      keys.join(","),
      ...array.map((row) => keys.map((k) => `"${row[k] ?? ""}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csv], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = "Finance-leads.csv";
    link.click();

    URL.revokeObjectURL(url);
  };

  const actionsMemo = useMemo(() => {
    return <Export onExport={() => downloadCSV(data)} />;
  }, [data]);

  /* ================= TABLE COLUMNS ================= */
  const columns = [
    { name: "Id", selector: (row) => row.id },
    { name: "Name", selector: (row) => row.name || "-" },
    { name: "Email", selector: (row) => row.email || "-" },
    { name: "Phone", selector: (row) => row.phone || "-" },
    { name: "Model", selector: (row) => row.model || "-" },
    { name: "Description", selector: (row) => row.comments || "-" },
    { name: "Outlet", selector: (row) => row.outlet || "-" },
    { name: "Purchase Time", selector: (row) => row.purchaseTime || "-" },
    { name: "Loan Amount", selector: (row) => row.formLoanAmount || "-" },
    { name: "Duration", selector: (row) => row.loanDuration || "-" },
    {
      name: "Date",
      selector: (row) =>
        row.timestamp?.toDate
          ? row.timestamp.toDate().toLocaleString()
          : row.timestamp || "-",
    },
  ];

  /* ================= UI ================= */
  return (
    <div className="flex flex-row h-screen">
      <Sidebar active={active} />

      <div className="flex-auto bg-gray-50 overflow-auto">
        <Navbar handleActive={handleActive} />

        <div className="mx-5 mt-5">
          <DataTable
            title="Finance Leads"
            columns={columns}
            data={filteredData}
            selectableRows
            selectableRowsHighlight
            progressPending={loading}
            progressComponent={<CustomLoader />}
            pagination
            fixedHeader
            fixedHeaderScrollHeight="100vh"
            customStyles={customStyles}
            highlightOnHover
            subHeader
            actions={actionsMemo}
            subHeaderComponent={
              <SearchComponent search={search} setSearch={setSearch} />
            }
          />
        </div>
      </div>
    </div>
  );
}

/* ================= SEARCH COMPONENT ================= */
const SearchComponent = ({ search, setSearch }) => {
  return (
    <div className="flex items-center justify-center">
      <input
        className="border-2 rounded mb-3 py-2 px-4 text-gray-700 font-light focus:outline-none"
        type="text"
        placeholder="Search by name, phone or email"
        value={search}
        style={{ width: "300px" }}
        onChange={(e) => setSearch(e.target.value)}
      />
    </div>
  );
};

export default FinanceLeads;
